import {Tooltip} from 'platform/components';
import {HStack, Text, ThemeColorTextPath} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useEffect, useState} from 'react';

import {always} from 'ramda';

import {getFixedTranslation} from '../../../utils/getFixedTranslation';

interface PerformanceResourceTiming extends PerformanceEntry {
  initiatorType: string;
  transferSize: number;
  encodedBodySize: number;
  decodedBodySize: number;
}

const isResourceTiming = (entry: PerformanceEntry): entry is PerformanceResourceTiming =>
  entry.entryType === 'resource' &&
  'transferSize' in entry &&
  'encodedBodySize' in entry &&
  'decodedBodySize' in entry;

const isJavaScriptResource = (resource: PerformanceResourceTiming): boolean => {
  // eslint-disable-next-line security/detect-unsafe-regex
  const JS_FILE_PATTERN = /\.js(\?.*)?$/i;
  const JS_INITIATOR_TYPES = ['script', 'link'];

  return JS_FILE_PATTERN.test(resource.name) || JS_INITIATOR_TYPES.includes(resource.initiatorType);
};

const getResourceSize = (resource: PerformanceResourceTiming): number => {
  const TRANSFER_SIZE_AVAILABLE = resource.transferSize > 0;
  const ENCODED_SIZE_AVAILABLE = resource.encodedBodySize > 0;

  if (TRANSFER_SIZE_AVAILABLE) {
    return resource.transferSize;
  }

  if (ENCODED_SIZE_AVAILABLE) {
    return resource.encodedBodySize;
  }

  return resource.decodedBodySize;
};

interface BundleStats {
  totalSize: number;
}

export function BundleSize() {
  const [bundleStats, setBundleStats] = useState<BundleStats>({
    totalSize: 0,
  });

  useEffect(function observeBundleSize() {
    const calculateBundleSize = () => {
      if (!window.performance) {
        return;
      }

      const resources = performance.getEntriesByType('resource');
      const jsResources = resources.filter(isResourceTiming).filter(isJavaScriptResource);
      const totalSize = jsResources.reduce((sum, resource) => sum + getResourceSize(resource), 0);

      setBundleStats({
        totalSize,
      });
    };

    calculateBundleSize();

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const hasNewJsResources = entries.some(
        (entry) => isResourceTiming(entry) && isJavaScriptResource(entry)
      );

      if (hasNewJsResources) {
        calculateBundleSize();
      }
    });

    observer.observe({entryTypes: ['resource']});

    return () => observer.disconnect();
  }, []);

  const BUNDLE_SIZE_IN_MB = (bundleStats.totalSize / 1024 / 1024).toFixed(2);

  const sizeColor = match<number, ThemeColorTextPath>(Number(BUNDLE_SIZE_IN_MB))
    .with(Pattern.number.gt(30), always('danger'))
    .with(Pattern.number.gt(15), always('warning'))
    .otherwise(always('success'));

  return (
    <Tooltip label={getFixedTranslation('page.settings.labels.bundleSize')}>
      <HStack spacing={1}>
        <Text color={sizeColor}>{BUNDLE_SIZE_IN_MB}</Text>
        <Text color="tertiary">MB</Text>
      </HStack>
    </Tooltip>
  );
}
